'use client';

import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/core';

export type CryptoProvider = 'BINANCE' | 'KUCOIN' | 'BYBIT';

const PROVIDERS: { id: CryptoProvider; name: string; img: string }[] = [
  { id: 'BINANCE', name: 'Binance Pay', img: '/assets/BinancePay.svg' },
  { id: 'KUCOIN', name: 'KuCoin', img: '/assets/Kucoin.svg' },
  { id: 'BYBIT', name: 'Bybit Pay', img: '/assets/BybitPay.svg' },
];

interface ProviderSelectionProps {
  baseAmount: number;
  selectedProvider: string | null;
  isSubmittingOrder: boolean;
  onSelectProvider: (id: CryptoProvider) => void;
  onSubmit: () => void;
  renderWarning: () => React.ReactNode;
}

export default function ProviderSelection({
  baseAmount,
  selectedProvider,
  isSubmittingOrder,
  onSelectProvider,
  onSubmit,
  renderWarning,
}: ProviderSelectionProps) {
  const isSubmitDisabled = !selectedProvider || isSubmittingOrder;

  return (
    <>
      <div className="flex-1 overflow-y-auto p-5 bg-white">
        <div className="space-y-4">
          <AmountDisplay amount={baseAmount} />

          <div className="grid grid-cols-3 gap-3">
            {PROVIDERS.map((p) => {
              const isSelected = selectedProvider === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => onSelectProvider(p.id)}
                  className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all overflow-hidden ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50 shadow-md shadow-emerald-100'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-0 right-0 w-6 h-6 bg-emerald-500 z-0" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}>
                      <svg className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  )}
                  <div className="relative z-10 h-10 flex items-center justify-center">
                    <img src={p.img} alt={p.name} className="h-full w-auto object-contain" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="text-xs text-slate-500 text-center leading-relaxed bg-slate-50 rounded-lg p-3 border border-slate-100">
            Pay with your crypto wallet. Amount is converted from LKR to USDT at current exchange rate.
          </div>

          {renderWarning()}
        </div>
      </div>

      <div className="p-4 border-t border-slate-200 bg-white shrink-0 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] z-10">
        <button
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          className="w-full bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-slate-800 text-white font-bold py-4 rounded-sm transition-all shadow-lg active:scale-[0.98] uppercase tracking-wider text-sm flex items-center justify-center gap-2"
        >
          {isSubmittingOrder ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
          ) : (
            <><>Pay with {PROVIDERS.find(p => p.id === selectedProvider)?.name || 'Crypto'}</><ChevronRight className="w-4 h-4 ml-1" /></>
          )}
        </button>
      </div>
    </>
  );
}

function AmountDisplay({ amount }: { amount: number }) {
  return (
    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center">
      <div className="relative z-10 flex flex-col items-center">
        <div className="text-[10px] md:text-[9px] font-bold uppercase tracking-[0.1em]" style={{ color: '#8BA3C0' }}>
          Total Checkout Amount
        </div>
        <div className="text-[36px] md:text-[46px] leading-[1] font-display text-slate-800 mt-2 flex items-baseline justify-center gap-1.5">
          <span className="text-lg text-slate-400 font-bold">Rs.</span>
          {amount.toFixed(2)}
        </div>
      </div>
    </div>
  );
}
